import type { CatalogProduct, MediaVariants } from '../types'
import { resolveCatalogProductPurchasability } from '../purchasability'
import { buildProductPathFromIdentity } from '../routing'

export const getProductImageUrl = (product: CatalogProduct): string =>
  product.mediaVariants?.large ?? product.mediaVariants?.medium ?? product.mediaVariants?.small ?? ''

const PRODUCT_IMAGE_VARIANTS: Array<{ key: keyof MediaVariants; width: number }> = [
  { key: 'small', width: 360 },
  { key: 'medium', width: 720 },
  { key: 'large', width: 1200 },
]

const buildSrcSet = (variants?: MediaVariants | null): string | undefined => {
  if (!variants) return undefined
  const seenUrls = new Set<string>()
  const entries: string[] = []
  for (const { key, width } of PRODUCT_IMAGE_VARIANTS) {
    const variantUrl = variants[key]
    if (!variantUrl || seenUrls.has(variantUrl)) continue
    seenUrls.add(variantUrl)
    entries.push(`${variantUrl} ${width}w`)
  }
  return entries.length > 0 ? entries.join(', ') : undefined
}

export const getProductImageSources = (
  product: CatalogProduct,
  sizes = '(min-width: 1280px) 30vw, (min-width: 768px) 46vw, 100vw',
): { src: string; srcSet?: string; sizes?: string } => {
  const src = getProductImageUrl(product)
  const srcSet = buildSrcSet(product.mediaVariants)
  return srcSet ? { src, srcSet, sizes } : { src }
}

export const formatPrice = (product: CatalogProduct): string | null => {
  const value = product.price
  if (typeof value !== 'number' || Number.isNaN(value)) return null
  return `${value.toFixed(2).replace('.', ',')} €`
}

export const buildProductPath = (categorySlugs: string[], product: CatalogProduct): string =>
  buildProductPathFromIdentity({
    publicCode: product.publicCode,
    slug: product.slug,
    categorySlugs,
  })

export const resolveAddToCartLabel = (product: CatalogProduct): string => {
  const purchasability = resolveCatalogProductPurchasability(product)
  if (purchasability.reason === 'out_of_stock') return 'Esaurito'
  if (purchasability.isPurchasable) return 'Aggiungi'
  return 'Non acquistabile'
}
